import type React from "react"

import { Box, Stack, Typography } from "@mui/material"

import type { EnduseDelta } from "./disaggregation.types"
import { enduseLabel } from "./enduse-colors"

/** Per-EULP delta vs the site's own seasonal baseline, diverging bars. */
export const EnduseDeltaBars: React.FC<{ enduses: EnduseDelta[] }> = ({
  enduses,
}) => {
  const withDelta = enduses.filter((e) => e.deltaKwh != null)
  if (withDelta.length === 0) {
    return (
      <Typography color="text.secondary" variant="body2">
        No seasonal-baseline peers for a comparison.
      </Typography>
    )
  }
  const max = Math.max(
    ...withDelta.map((e) => Math.abs(e.deltaKwh as number)),
    1
  )
  const ranked = [...withDelta].sort(
    (a, b) => Math.abs(b.deltaKwh as number) - Math.abs(a.deltaKwh as number)
  )

  return (
    <Stack spacing={1}>
      {ranked.map((e) => {
        const d = e.deltaKwh as number
        const pct = Math.min(100, (Math.abs(d) / max) * 100)
        const over = d >= 0
        return (
          <Stack
            direction="row"
            key={e.enduse}
            spacing={1.5}
            sx={{ alignItems: "center" }}
          >
            <Typography sx={{ width: 120, flexShrink: 0 }} variant="dataLabel">
              {enduseLabel(e.enduse)}
            </Typography>
            {/* center-origin diverging track */}
            <Box sx={{ flex: 1, display: "flex", justifyContent: "center" }}>
              <Box
                sx={{
                  width: "50%",
                  display: "flex",
                  justifyContent: "flex-end",
                }}
              >
                {!over ? (
                  <Box
                    sx={{
                      width: `${pct}%`,
                      height: 10,
                      borderRadius: "9999px 0 0 9999px",
                      bgcolor: (t) => t.palette.success.main,
                    }}
                  />
                ) : null}
              </Box>
              <Box sx={{ width: "50%" }}>
                {over ? (
                  <Box
                    sx={{
                      width: `${pct}%`,
                      height: 10,
                      borderRadius: "0 9999px 9999px 0",
                      bgcolor: (t) => t.palette.error.main,
                    }}
                  />
                ) : null}
              </Box>
            </Box>
            <Typography
              sx={{
                minWidth: 96,
                flexShrink: 0,
                textAlign: "right",
                fontWeight: "bold",
                fontVariantNumeric: "tabular-nums",
                color: over ? "error.main" : "success.main",
              }}
              variant="dataLabel"
            >
              {over ? "+" : ""}
              {Math.round(d).toLocaleString()} kWh
            </Typography>
          </Stack>
        )
      })}
    </Stack>
  )
}
